import React, { useState } from 'react';
import { Moon, Smartphone, BookOpen, Check, X, Clock, Shield, RefreshCw, List, Calendar } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Section } from '../components/ui/Section';
import { Accordion, AccordionItem } from '../components/ui/Accordion';
import { Modal } from '../components/ui/Modal';
export function LandingPage() {
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const scrollToPricing = () => {
    const element = document.getElementById('offer');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-[#FDFBF7] text-[#1F2937]" data-id="element-24">
      {/* Hero Section */}
      <div className="pt-16 pb-16 md:pt-24 md:pb-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-25">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-26">
          {/* Left Column (Desktop) / Mixed Order (Mobile) */}
          <div className="flex flex-col text-center lg:text-left items-center lg:items-start" data-id="element-27">
            {/* 1. Badge */}
            <span className="inline-block py-1 px-3 rounded-full bg-blue-100 text-blue-800 text-xs font-semibold tracking-wide uppercase mb-6 order-1" data-id="element-28">
              Workbook Digital
            </span>

            {/* 2. Title */}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-[#1F2937] leading-tight mb-6 order-2" data-id="element-29">
              Te estás yendo a dormir con la{' '}
              <span className="text-[#1d4ed8] italic" data-id="element-30">cabeza prendida</span>.
            </h1>

            {/* 3. Mockup (Mobile Only) */}
            <div className="relative w-full max-w-md mx-auto shadow-2xl rounded-xl overflow-hidden border-4 border-white mb-8 lg:hidden order-3" data-id="element-31">
              <img src="https://cdn.magicpatterns.com/uploads/9YdsXsUN1QhfnvUuMAoLEm/Mockup_-_Producto_Principal.png" alt="7 Días Sin Scroll Workbook en iPad" className="w-full h-auto object-cover" data-id="element-32" />
            </div>

            {/* 4. Subtitle */}
            <p className="text-lg md:text-xl text-gray-600 max-w-xl mb-10 leading-relaxed order-4" data-id="element-33">
              No es falta de disciplina. Es un loop. Recuperá tu descanso en 7
              días sin desaparecer de internet.
            </p>

            {/* 5. Button */}
            <div className="flex flex-col sm:flex-row gap-4 order-5 w-full sm:w-auto" data-id="element-34">
              <Button onClick={scrollToPricing} icon className="w-full sm:w-auto" data-id="element-35">
                Quiero recuperar mi sueño
              </Button>
            </div>
          </div>

          {/* Right Column (Desktop Only) */}
          <div className="hidden lg:block relative max-w-lg mx-auto shadow-2xl rounded-xl overflow-hidden border-4 border-white" data-id="element-36">
            <img src="https://cdn.magicpatterns.com/uploads/9YdsXsUN1QhfnvUuMAoLEm/Mockup_-_Producto_Principal.png" alt="7 Días Sin Scroll Workbook en iPad" className="w-full h-auto object-cover" data-id="element-37" />
          </div>
        </div>
      </div>

      {/* The Problem (The Loop) */}
      <Section background="white" className="border-y border-gray-100" data-id="element-38">
        <div className="prose prose-lg prose-slate mx-auto" data-id="element-39">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center" data-id="element-40">
            Anoche dijiste "cinco minutos".
          </h2>
          <p className="lead text-xl text-gray-600 mb-8 font-serif italic text-center" data-id="element-41">
            Y cuando levantaste la vista, ya era tarde. Otra vez.
          </p>

          <div className="bg-[#FDFBF7] p-8 rounded-2xl my-12 border border-gray-100" data-id="element-42">
            <p className="mb-4 font-medium" data-id="element-43">No porque seas débil.</p>
            <p className="mb-0" data-id="element-44">
              Porque el scroll es un loop: te da alivio rápido… y después te
              cobra con sueño, ansiedad y esa culpa chiquita que no le contás a
              nadie.
            </p>
          </div>

          <p data-id="element-45">
            Este workbook no te pide que desaparezcas de Internet. Te pide una
            cosa más simple (y más difícil): recuperar el control en los 3
            momentos donde el scroll más te rompe: la noche, la primera hora y
            las micro-pausas.
          </p>

          <h3 className="text-2xl font-bold mt-12 mb-6" data-id="element-46">
            Sos una persona en un loop
          </h3>
          <p data-id="element-47">Funciona así:</p>
          <ul className="list-none space-y-4 pl-0 my-8" data-id="element-48">
            <li className="flex items-start" data-id="element-49">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center font-bold mr-4" data-id="element-50">
                1
              </span>
              <span data-id="element-51">
                <strong data-id="element-52">Incomodidad:</strong> ansiedad, aburrimiento, cansancio,
                soledad.
              </span>
            </li>
            <li className="flex items-start" data-id="element-53">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center font-bold mr-4" data-id="element-54">
                2
              </span>
              <span data-id="element-55">
                <strong data-id="element-56">Acción:</strong> abrís redes sin pensar.
              </span>
            </li>
            <li className="flex items-start" data-id="element-57">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center font-bold mr-4" data-id="element-58">
                3
              </span>
              <span data-id="element-59">
                <strong data-id="element-60">Recompensa:</strong> alivio rápido.
              </span>
            </li>
            <li className="flex items-start" data-id="element-61">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center font-bold mr-4" data-id="element-62">
                4
              </span>
              <span data-id="element-63">
                <strong data-id="element-64">Costo:</strong> sueño, foco, calma.
              </span>
            </li>
            <li className="flex items-start" data-id="element-65">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center font-bold mr-4" data-id="element-66">
                5
              </span>
              <span data-id="element-67">
                <strong data-id="element-68">Resultado:</strong> culpa → más incomodidad → repetís.
              </span>
            </li>
          </ul>

          <p className="font-medium text-center text-xl mt-12" data-id="element-69">
            No te falta motivación. Te falta sistema.
          </p>
        </div>
      </Section>

      {/* The Solution */}
      <Section background="blue" className="text-center" data-id="element-70">
        <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-71">
          Presento: 7 Días Sin Scroll
        </h2>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-12" data-id="element-72">
          Un workbook práctico para cortar el scroll compulsivo sin desaparecer
          de Internet. No es un libro inspiracional. Es un protocolo simple.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-left" data-id="element-73">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-blue-100" data-id="element-74">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-4" data-id="element-75">
              <Shield className="w-5 h-5" data-id="element-76" />
            </div>
            <h3 className="font-bold text-lg mb-2" data-id="element-77">Día 0: Auditoría</h3>
            <p className="text-sm text-gray-600" data-id="element-78">
              Entendé tus gatillos reales y preparate para no abandonar.
            </p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-blue-100" data-id="element-79">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-4" data-id="element-80">
              <List className="w-5 h-5" data-id="element-81" />
            </div>
            <h3 className="font-bold text-lg mb-2" data-id="element-82">7 Reglas Mínimas</h3>
            <p className="text-sm text-gray-600" data-id="element-83">
              Fricción inteligente sin extremismo. Reglas que podés cumplir.
            </p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-blue-100" data-id="element-84">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-4" data-id="element-85">
              <RefreshCw className="w-5 h-5" data-id="element-86" />
            </div>
            <h3 className="font-bold text-lg mb-2" data-id="element-87">7 Reemplazos</h3>
            <p className="text-sm text-gray-600" data-id="element-88">
              Micro-hábitos de 3–10 min para cuando el dedo va solo.
            </p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-blue-100" data-id="element-89">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-4" data-id="element-90">
              <Calendar className="w-5 h-5" data-id="element-91" />
            </div>
            <h3 className="font-bold text-lg mb-2" data-id="element-92">Tracker Diario</h3>
            <p className="text-sm text-gray-600" data-id="element-93">
              Solo 2 minutos al día para medir tu progreso real.
            </p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-blue-100 md:col-span-2 lg:col-span-2" data-id="element-94">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-4" data-id="element-95">
              <Clock className="w-5 h-5" data-id="element-96" />
            </div>
            <h3 className="font-bold text-lg mb-2" data-id="element-97">Protocolo Anti-recaída</h3>
            <p className="text-sm text-gray-600" data-id="element-98">
              Un sistema para volver en 24 hs sin castigarte cuando "fallás".
            </p>
          </div>
        </div>
      </Section>

      {/* Audience Split */}
      <Section data-id="element-99">
        <h2 className="text-3xl font-bold text-center mb-12" data-id="element-100">
          ¿Para quién es?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-101">
          <div className="bg-white p-8 rounded-2xl shadow-sm border-t-4 border-green-500" data-id="element-102">
            <h3 className="text-xl font-bold mb-6 flex items-center" data-id="element-103">
              <Check className="w-6 h-6 text-green-500 mr-2" data-id="element-104" />
              Es para vos si...
            </h3>
            <ul className="space-y-4" data-id="element-105">
              <li className="flex items-start" data-id="element-106">
                <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-107" />
                <span className="text-gray-700" data-id="element-108">
                  Sos mujer 20–25 y vivís con el celu.
                </span>
              </li>
              <li className="flex items-start" data-id="element-109">
                <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-110" />
                <span className="text-gray-700" data-id="element-111">
                  Te acostás con el celu y se te va la noche.
                </span>
              </li>
              <li className="flex items-start" data-id="element-112">
                <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-113" />
                <span className="text-gray-700" data-id="element-114">
                  Querés una solución corta, concreta y medible.
                </span>
              </li>
              <li className="flex items-start" data-id="element-115">
                <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-116" />
                <span className="text-gray-700" data-id="element-117">
                  No querés sermones ni vivir sin redes.
                </span>
              </li>
            </ul>
          </div>

          <div className="bg-gray-50 p-8 rounded-2xl border-t-4 border-gray-400" data-id="element-118">
            <h3 className="text-xl font-bold mb-6 flex items-center text-gray-500" data-id="element-119">
              <X className="w-6 h-6 text-gray-400 mr-2" data-id="element-120" />
              No es para vos si...
            </h3>
            <ul className="space-y-4" data-id="element-121">
              <li className="flex items-start" data-id="element-122">
                <X className="w-5 h-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" data-id="element-123" />
                <span className="text-gray-600" data-id="element-124">
                  Querés "dejar redes para siempre".
                </span>
              </li>
              <li className="flex items-start" data-id="element-125">
                <X className="w-5 h-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" data-id="element-126" />
                <span className="text-gray-600" data-id="element-127">
                  No estás dispuesta a cambiar 2–3 hábitos básicos.
                </span>
              </li>
              <li className="flex items-start" data-id="element-128">
                <X className="w-5 h-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" data-id="element-129" />
                <span className="text-gray-600" data-id="element-130">
                  Buscás terapia clínica o tratamiento médico.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </Section>

      {/* The Offer */}
      <Section id="offer" background="white" className="border-y border-gray-100" data-id="element-131">
        <div className="max-w-4xl mx-auto bg-[#FDFBF7] rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100 text-center" data-id="element-132">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-133">
            Empezá tu reset hoy
          </h2>
          <p className="text-gray-600 mb-8" data-id="element-134">
            Descarga instantánea. Empezá el Día 0 en 5 minutos.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left mb-10" data-id="element-135">
            <div data-id="element-136">
              <h3 className="font-bold text-lg mb-4 text-[#1d4ed8]" data-id="element-137">
                Lo que recibís:
              </h3>
              <ul className="space-y-3" data-id="element-138">
                <li className="flex items-center" data-id="element-139">
                  <BookOpen className="w-5 h-5 text-gray-400 mr-3" data-id="element-140" />
                  <span data-id="element-141">Workbook completo "7 Días Sin Scroll"</span>
                </li>
                <li className="flex items-center" data-id="element-142">
                  <Check className="w-5 h-5 text-gray-400 mr-3" data-id="element-143" />
                  <span data-id="element-144">Trackers diarios (7 días) + plan 30 días</span>
                </li>
                <li className="flex items-center" data-id="element-145">
                  <Check className="w-5 h-5 text-gray-400 mr-3" data-id="element-146" />
                  <span data-id="element-147">Auditoría Día 0 (gatillos reales)</span>
                </li>
                <li className="flex items-center" data-id="element-148">
                  <Check className="w-5 h-5 text-gray-400 mr-3" data-id="element-149" />
                  <span data-id="element-150">Lista de Reemplazos listos para usar</span>
                </li>
                <li className="flex items-center" data-id="element-151">
                  <Check className="w-5 h-5 text-gray-400 mr-3" data-id="element-152" />
                  <span data-id="element-153">Protocolo anti-recaída</span>
                </li>
              </ul>
            </div>
            <div className="flex flex-col justify-center items-center bg-white p-6 rounded-xl border border-gray-100" data-id="element-154">
              <span className="text-gray-400 line-through text-lg" data-id="element-155">
                49.990 ARS
              </span>
              <span className="text-5xl font-bold text-[#1F2937] mb-2" data-id="element-156">
                19.990
                <span className="text-xl text-gray-500 font-normal" data-id="element-157"> ARS</span>
              </span>
              <span className="text-sm text-green-600 font-medium bg-green-50 px-2 py-1 rounded mb-6" data-id="element-158">
                Oferta lanzamiento
              </span>
              <Button fullWidth icon href="https://tuzonapro.shop/cart/47458032386235:1" data-id="element-159">
                Descargar Ahora
              </Button>
              <p className="text-xs text-gray-400 mt-4" data-id="element-160">
                Pago seguro. Entrega inmediata por email.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section data-id="element-161">
        <h2 className="text-3xl font-bold text-center mb-12" data-id="element-162">
          Preguntas Frecuentes
        </h2>
        <div className="max-w-2xl mx-auto" data-id="element-163">
          <Accordion data-id="element-164">
            <AccordionItem question="¿Y si uso redes para laburo?" answer="Perfecto. No es anti-redes. Es anti-scroll automático. Vas a aprender a entrar, hacer lo que tenés que hacer, y salir sin quedarte atrapada." data-id="element-165" />
            <AccordionItem question="¿Y si fallo el día 3?" answer="No 'fallaste'. Activás el protocolo anti-recaída y reiniciás en 24 horas con una regla mínima. El sistema está diseñado asumiendo que vas a tener deslices." data-id="element-166" />
            <AccordionItem question="¿Esto es terapia?" answer="No. Es un sistema de hábitos y fricción para recuperar control. Si sentís que tu ansiedad requiere atención clínica, por favor consultá a un profesional de la salud mental." data-id="element-167" />
            <AccordionItem question="¿En qué formato viene?" answer="Es un PDF interactivo diseñado para leer en celular, tablet o computadora. También podés imprimir los trackers si preferís escribir a mano." data-id="element-168" />
          </Accordion>
        </div>
      </Section>

      {/* Final Footer */}
      <footer className="bg-[#1F2937] text-white py-16 px-4 text-center" data-id="element-169">
        <div className="max-w-2xl mx-auto" data-id="element-170">
          <Moon className="w-12 h-12 text-blue-400 mx-auto mb-6" data-id="element-171" />
          <h2 className="text-3xl font-serif font-bold mb-6" data-id="element-172">
            Si estás cansada de acostarte con la cabeza prendida...
          </h2>
          <p className="text-gray-300 mb-8 text-lg" data-id="element-173">
            Probá un reset realista, corto y sin extremismos.
          </p>
          <Button variant="primary" onClick={scrollToPricing} className="bg-white !text-[#1F2937] hover:bg-gray-100" data-id="element-174">
            Descargar "7 Días Sin Scroll"
          </Button>

          <div className="mt-12 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-center items-center gap-6 text-sm text-gray-500" data-id="element-175">
            <p data-id="element-176">
              © {new Date().getFullYear()} 7 Días Sin Scroll. Todos los
              derechos reservados.
            </p>
            <div className="flex gap-6" data-id="element-177">
              <button onClick={() => setIsPrivacyOpen(true)} className="hover:text-white transition-colors" data-id="element-178">
                Política de Privacidad
              </button>
              <button onClick={() => setIsTermsOpen(true)} className="hover:text-white transition-colors" data-id="element-179">
                Términos y Condiciones
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <Modal isOpen={isPrivacyOpen} onClose={() => setIsPrivacyOpen(false)} title="Política de Privacidad" data-id="element-180">
        <p className="mb-4" data-id="element-181">
          Esta Política de Privacidad describe cómo recopilamos, usamos y
          protegemos tu información personal.
        </p>
        <h4 className="font-bold mb-2" data-id="element-182">1. Información que recopilamos</h4>
        <p className="mb-4" data-id="element-183">
          Recopilamos información que nos proporcionas directamente, como tu
          nombre y dirección de correo electrónico cuando realizas una compra.
        </p>
        <h4 className="font-bold mb-2" data-id="element-184">2. Uso de la información</h4>
        <p className="mb-4" data-id="element-185">
          Utilizamos tu información para procesar tu pedido, enviarte el
          producto digital y comunicarnos contigo sobre tu compra.
        </p>
        <h4 className="font-bold mb-2" data-id="element-186">3. Protección de datos</h4>
        <p className="mb-4" data-id="element-187">
          Tomamos medidas razonables para proteger tu información personal
          contra pérdida, robo y uso no autorizado.
        </p>
      </Modal>

      <Modal isOpen={isTermsOpen} onClose={() => setIsTermsOpen(false)} title="Términos y Condiciones" data-id="element-188">
        <p className="mb-4" data-id="element-189">
          Al comprar y descargar "7 Días Sin Scroll", aceptas los siguientes
          términos y condiciones.
        </p>
        <h4 className="font-bold mb-2" data-id="element-190">1. Propiedad Intelectual</h4>
        <p className="mb-4" data-id="element-191">
          Este workbook es propiedad intelectual de 7 Días Sin Scroll. Está
          prohibida su reproducción, distribución o venta sin autorización
          expresa.
        </p>
        <h4 className="font-bold mb-2" data-id="element-192">2. Uso Personal</h4>
        <p className="mb-4" data-id="element-193">
          La compra te otorga una licencia para uso personal y no comercial. No
          puedes compartir el archivo con terceros.
        </p>
        <h4 className="font-bold mb-2" data-id="element-194">3. Política de Reembolso</h4>
        <p className="mb-4" data-id="element-195">
          Debido a la naturaleza digital del producto, no ofrecemos reembolsos
          una vez que el archivo ha sido descargado, salvo excepciones legales
          aplicables.
        </p>
      </Modal>
    </div>;
}